import React, { useState, useContext, useEffect } from 'react';

import {
  getGroupFirstFlow,
  getGroupLastFlow
} from '../../../../helpers/getGroupEdgeFlows/getGroupEdgeFlows';
import SidePropertiesContainer from '../SidePropertiesContainer/SidePropertiesContainer';
import SideProperties from './components/SideProperties/SideProperties';
import SurveyBuilderContext from '../../../../SurveyBuilderContext';
import GroupHeader from './components/GroupHeader/GroupHeader';
import GroupFooter from './components/GroupFooter/GroupFooter';
import FlowLogicJump from '../FlowLogicJump/FlowLogicJump';
import DropZone from '../DropZone';

import {
  getPreviousNeigboingFlow,
  getNextNeigboingFlow
} from '../../../../helpers/getNeigboringFlows/getNeigboringFlows';

import styles from './Group.module.css';
import GroupAndRandomiseChild from './components/GroupAndRandomise/GroupAndRandomiseChild/GroupAndRandomiseChild';
import GroupAndRandomiseParent from './components/GroupAndRandomise/GroupAndRandomiseParent/GroupAndRandomiseParent';

export default function({ group, elements }) {
  const [isActiveGroup, setIsActiveGroup] = useState(false);
  const { state, actions } = useContext(SurveyBuilderContext);
  const [groupNameRef, setGroupNameRef] = useState(null);

  useEffect(
    () => {
      if (
        isActiveGroup &&
        ((state.activeFlow && state.activeFlow.id !== group.id) ||
          !state.activeFlow)
      ) {
        setIsActiveGroup(false);
      }
      if (
        !isActiveGroup &&
        state.activeFlow &&
        state.activeFlow.id === group.id
      ) {
        setIsActiveGroup(true);
      }
    },
    [state.activeFlow]
  );

  let surveyListGroup = null;
  surveyListGroup = state.surveyLists.find(
    sLQ => sLQ.group && sLQ.group.id === group.id
  );
  if (surveyListGroup) {
    surveyListGroup = surveyListGroup.group;
  }
  if (!surveyListGroup) {
    const surveyListParentGroup = state.surveyLists.find(
      sLQ =>
        sLQ.group &&
        sLQ.group.parentGroup &&
        sLQ.group.parentGroup.id === group.id
    );
    if (surveyListParentGroup) {
      surveyListGroup = surveyListParentGroup.group.parentGroup;
    }
  }

  // Check if it is group only
  if (
    !surveyListGroup &&
    state &&
    state.survey &&
    state.survey.content &&
    state.survey.content.groups &&
    state.survey.content.groups.some(g => g.id === group.id)
  ) {
    const g = state.survey.content.groups.find(ig => ig.id === group.id);

    if (g) {
      const newG = { ...g };

      // eslint-disable-next-line no-restricted-syntax
      for (const ig of state.survey.content.groups) {
        if (ig.flows.includes(group.id)) {
          newG.nestingLevel = 2;
          newG.group = ig;

          break;
        }
      }

      if (!newG.nestingLevel) {
        newG.nestingLevel = 1;
      }

      surveyListGroup = newG;
    }
  }

  if (!surveyListGroup) return null;

  const isGroupAndRandomiseParentGroup =
    surveyListGroup && surveyListGroup.isGroupAndRandomise;
  const isGroupAndRandomiseChildGroupPlaceholder =
    surveyListGroup &&
    surveyListGroup.group &&
    surveyListGroup.group.isGroupAndRandomise;
  const isGroupAndRandomiseChildGroup =
    surveyListGroup &&
    surveyListGroup.parentGroup &&
    surveyListGroup.parentGroup.isGroupAndRandomise;

  const firstGroupQuestionId = getGroupFirstFlow(state.survey.content, group);
  const firstGroupQuestion = state.surveyLists.find(
    sL => sL.id === firstGroupQuestionId
  );

  const lastGroupQuestionId = getGroupLastFlow(state.survey.content, group);
  const lastGroupQuestion = state.surveyLists.find(
    sL => sL.id === lastGroupQuestionId
  );

  const isFirstGroupChildFlowQuestion =
    firstGroupQuestion &&
    firstGroupQuestion.group &&
    firstGroupQuestion.group.nestingLevel === 1 &&
    firstGroupQuestion.group.parent;
  const isLastGroupChildFlowQuestion =
    lastGroupQuestion &&
    lastGroupQuestion.group &&
    lastGroupQuestion.group.nestingLevel === 1 &&
    lastGroupQuestion.group.parent;

  let containerStyle = styles.groupContainer;
  if (
    surveyListGroup &&
    surveyListGroup.parent &&
    !isGroupAndRandomiseParentGroup
  ) {
    containerStyle = `${containerStyle} ${styles.groupParentTopContainer} ${
      styles.groupContainer
    }`;
  }
  if (isActiveGroup) {
    containerStyle = `${containerStyle} ${styles.active}`;
  }
  if (surveyListGroup && surveyListGroup.nestingLevel === 2) {
    containerStyle = `${containerStyle} ${styles.childGroup}`;
  }
  if (isFirstGroupChildFlowQuestion) {
    containerStyle = `${containerStyle} ${styles.firstChildQuestion}`;
  }
  if (isLastGroupChildFlowQuestion) {
    containerStyle = `${containerStyle} ${styles.lastChildQuestion}`;
  }

  const nextNeigboingFlow = getNextNeigboingFlow(
    surveyListGroup,
    state.surveyLists
  );
  const isNextGroup = nextNeigboingFlow && nextNeigboingFlow.nestingLevel;

  const previousNeigboingFlow = getPreviousNeigboingFlow(
    surveyListGroup,
    state.surveyLists
  );
  const isPreviousGroup =
    previousNeigboingFlow && previousNeigboingFlow.nestingLevel;

  const showGroupFooter =
    lastGroupQuestion &&
    (!lastGroupQuestion.additionalInformation ||
      !lastGroupQuestion.additionalInformation.isNextGroup) &&
    (!surveyListGroup.additionalInformation ||
      !surveyListGroup.additionalInformation.nestedBottomEdgeGroup) &&
    !isNextGroup &&
    nextNeigboingFlow;

  const hideGroupHeaderActions =
    (surveyListGroup &&
      surveyListGroup.additionalInformation &&
      surveyListGroup.additionalInformation.nestedTopEdgeGroup) ||
    !previousNeigboingFlow;

  const hideBottomDropZone =
    surveyListGroup &&
    surveyListGroup.additionalInformation &&
    surveyListGroup.additionalInformation.nestedBottomEdgeGroup;

  const showTopDropZone =
    (surveyListGroup &&
      hideGroupHeaderActions &&
      surveyListGroup.nestingLevel === 2) ||
    isPreviousGroup ||
    !previousNeigboingFlow; // Group as first survey element

  const isTopDropZoneWithReducedHeight =
    isPreviousGroup ||
    (!previousNeigboingFlow &&
      surveyListGroup &&
      surveyListGroup.additionalInformation &&
      !surveyListGroup.additionalInformation.nestedTopEdgeGroup);

  const surveyStructure = {
    content: state.survey.content,
    questions: state.survey.questions
  };

  // Group and randomise parent group
  if (group && isGroupAndRandomiseParentGroup) {
    return (
      <GroupAndRandomiseParent
        isActiveGroup={isActiveGroup}
        actions={actions}
        group={group}
        setGroupNameRef={setGroupNameRef}
        containerStyle={containerStyle}
        state={state}
        elements={elements}
        surveyStructure={surveyStructure}
        groupNameRef={groupNameRef}
        surveyListGroup={surveyListGroup}
      />
    );
  }

  // Group and randomise child group placeholder
  if (
    group &&
    (isGroupAndRandomiseChildGroup || isGroupAndRandomiseChildGroupPlaceholder)
  ) {
    return (
      <GroupAndRandomiseChild
        actions={actions}
        isActiveGroup={isActiveGroup}
        group={group}
        state={state}
        surveyStructure={surveyStructure}
        surveyListGroup={surveyListGroup}
        isGroupAndRandomiseChildGroup={isGroupAndRandomiseChildGroup}
        groupNameRef={groupNameRef}
        setGroupNameRef={setGroupNameRef}
        elements={elements}
      />
    );
  }

  // For two groups in a row, Group actions are visible only in GroupHeader
  return (
    <div>
      {showTopDropZone && (
        <DropZone
          droppableId={`dropzone-${group.id}-0`}
          reducedHeight={isTopDropZoneWithReducedHeight}
        />
      )}
      <GroupHeader
        api={null}
        setGroupNameRef={setGroupNameRef}
        survey={null}
        group={group}
        isActiveGroup={isActiveGroup}
        actions={actions}
        hideGroupHeaderActions={hideGroupHeaderActions}
        surveyListGroup={surveyListGroup}
        surveyLists={state.surveyLists}
        surveyStructure={surveyStructure}
      />
      <div
        className={
          isActiveGroup
            ? `${styles.groupWrapper} ${styles.active}`
            : styles.groupWrapper
        }
      >
        <div className={containerStyle}>{elements}</div>
        {surveyListGroup && !surveyListGroup.parent && (
          <div
            className={
              isActiveGroup
                ? `${styles.verticalGroupLine} ${styles.active}`
                : styles.verticalGroupLine
            }
          />
        )}
        {isActiveGroup && (
          <SidePropertiesContainer top={0} title="Edit group">
            <SideProperties
              actions={actions}
              group={group}
              surveyLists={state.surveyLists}
              surveyStructure={surveyStructure}
              groupNameRef={groupNameRef}
              surveyListGroup={surveyListGroup}
            />
          </SidePropertiesContainer>
        )}
      </div>
      {isActiveGroup && (
        <FlowLogicJump
          key="flow-logic-jump"
          flow={group}
          surveyLists={state.surveyLists}
          actions={actions}
          surveyStructure={surveyStructure}
          isActive={isActiveGroup}
          isGroup
        />
      )}
      {showGroupFooter && (
        <GroupFooter
          surveyListGroup={surveyListGroup}
          surveyLists={state.surveyLists}
          surveyStructure={surveyStructure}
          actions={actions}
        />
      )}
      {hideBottomDropZone && surveyListGroup.nestingLevel === 2 && (
        <DropZone droppableId={`dropzone-${group.id}-1`} />
      )}
    </div>
  );
};
